import { Component, ViewChild } from '@angular/core';
import app from 'App';
import timer from 'app/managecustomer/Timer';
import permissions from 'app/models/Permissions';
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { ViewLocatorService } from '../../services/view-locator.service';
import { CustomerService } from '../../services/customer.service';
import { CustomerTouchService } from '../../services/customer-touch.service';
import { StoreService } from '../../services/store.service';
import { WebsiteService } from '../../services/website.service';
import ProductEnum = Boo.Objects.Enums.ProductEnum;
import { SessionStorageService } from '../../services/session-storage.service';
import Utils from '../../shared/utils';
import { forkJoin } from 'rxjs';
import { CustomerMapping } from '../../shared/legacy-constants';
import { CustomerInformationComponent } from './CustomerInformation/CustomerInformation.component';
import System from 'framework/System';
import { TabPanelDisplayMode } from 'app/components/enums/TabPanelDisplayMode';
import { TabPanelComponent } from '../../shared/components/TabPanel/tab-panel/tab-panel.component';
import { NotesAndTicketsComponent } from './notes-and-tickets/notes-and-tickets.component';
import { supportsReceipts } from '../../shared/models/partner-collections';

@Component({
  selector: 'app-components-managecustomer',
  templateUrl: './ManageCustomer.component.html'
})
export class ManageCustomerComponent implements IActivatable {
  @ViewChild(CustomerInformationComponent) customerInformationComponent: CustomerInformationComponent;
  @ViewChild(TabPanelComponent) tabPanel: TabPanelComponent;
  @ViewChild(NotesAndTicketsComponent) notesAndTickets: NotesAndTicketsComponent;

  tabPanelDisplayMode = TabPanelDisplayMode;

  user: Boo.Objects.User;
  partnerUser: Boo.Objects.PartnerUser;
  partner: Boo.Objects.Partner;
  isLoading = false;
  customer: Boo.Objects.Customer;
  customerObservable: CustomerObservable;
  customerId: number;
  isTimeoutConfirmVisible = false;
  shouldLogCustomerTouch = true;
  customerTimoutInterval: number = null;
  title: string;
  showStoreLink = true;
  partnerName = '';
  hasOnlyEverHadBusinessProfileManagement = false;
  partnerSupportsReceipts = false;
  hasWebsite = false;
  private dfd: any;

  constructor(
    private viewLocatorService: ViewLocatorService,
    private customerService: CustomerService,
    private customerTouchService: CustomerTouchService,
    private storeService: StoreService,
    private websiteService: WebsiteService,
    private sessionStorageService: SessionStorageService) { }

  public activate(options: any) {
    if (!options || !options.customerId) {
      toastr.error('Unable to Open Manage Customer');
      this.close();
      return;
    }

    this.viewLocatorService.setDisplayedRoute(`customerservice/managecustomer/${options.customerId}`);
    this.isLoading = true;
    this.customerId = options.customerId;
    this.title = `Manage Customer (${this.customerId})`;

    return Utils.wrapDfd(forkJoin({
      user: this.sessionStorageService.getUser(),
      partner: this.sessionStorageService.getPartner(),
      partnerUser: this.sessionStorageService.getPartnerUser(),
      customer: this.customerService.get(this.customerId),
      websites: this.websiteService.getManyByCustomerId(this.customerId)
    })).then(({ user, partner, partnerUser, customer, websites }) => {
      if (!customer || customer.StatusId < 2) {
        return System.autoRejectedPromise('Cannot open customer.');
      }

      const teams: Boo.Objects.PartnerUserTeam[] = [];
      const hasCustomerServiceUser = user.PartnerUsers.some((pu) => { return pu.UserLevelId === Boo.Objects.Enums.UserLevelEnum.CustomerService; });
      for (const pUser of user.PartnerUsers) {
        if (pUser.Teams) {
          for (const pUTeam of pUser.Teams) {
            teams.push(pUTeam);
          }
        }
      }

      if (partner.PartnerId === Boo.Objects.Enums.PartnerEnum.Boostability) {
        if (customer.Team) {
          const hasAllTeams = Utils.hasPermission(partner, user.PartnerUsers, permissions.HasAllTeams, user);
          const isInCustomerTeam = teams.some(uTeam => uTeam.TeamId === customer.Team.TeamId);
          if (!hasAllTeams && !isInCustomerTeam) {
            let message = "You don't have access to open this customer.";
            if (!hasCustomerServiceUser) {
              message += '<br/><br/>Add a Customer Service User and try again.';
            }

            return System.autoRejectedPromise(message);
          }
        }
      } else if (!hasCustomerServiceUser) {
        toastr.warning("You currently do not have a <strong>Customer Service</strong> user. This will prevent you from making updates to this customer's account.");
      }

      app.isHighProfile(customer.IsHighProfile);
      app.highProfileReason('');
      if (customer.IsHighProfile) {
        bootbox.alert(`<h4>This customer is high profile.</h4>  <div class="form-group"><label>Reason</label><div>${customer.HighProfileReason}</div></div>`);
        app.highProfileReason(customer.HighProfileReason + " (" + customer.HighProfileDate.toLocaleDateString('en-US') + ")");
      }

      const showAdultContentWarning = customer.HasAdultContent && !partnerUser.CanViewAdultContent;
      if (showAdultContentWarning) {
        const adultMsg = '<h4>This customer\'s website contains adult content</h4>  <div>ie - nudity, sexual content, provocative images<div>';
        bootbox.confirm(adultMsg,
          (result: boolean) => {
            if (result === false) {
              this.close(true);
            }
          });
      }

      app.hasAdultContent(showAdultContentWarning);

      this.customer = customer;
      this.customerObservable = ko.mapping.fromJS(customer, CustomerMapping);
      this.user = user;
      this.partner = customer.Partner;
      this.partnerName = customer.Partner.Name;
      this.partnerUser = partnerUser;
      this.setChildPageVisibility(partnerUser, partner, websites);
      // Users can open "EditTicket" from help next customer.
      // The app needs to open managecustomer prior to opening and editing the ticket
      // To handle this the ticket needs to be passed from help next customer to managecustomer to notesAndTickets to editTicket
      if (_.isNumber(options.ticketIdToEdit)) {
        this.openTicket(options.ticketIdToEdit);
      }
      // Start the timing operations
      this.startTimer();
    }).fail((displayMessage) => {
      this.close();
      toastr.error(displayMessage);
    }).always(() => {
      this.isLoading = false;
    });
  }

  deactivate() {
    this.logCustomerTouch('Closing Customer');
    if (this.customerTimoutInterval) {
      clearInterval(this.customerTimoutInterval);
    }

    app.hasAdultContent(false);
    app.isHighProfile(false);
    app.highProfileReason('');
  }

  openTab(title: string): void {
    this.tabPanel.selectTabByTitle(title);
  }

  close(closeAll?: boolean) {
    if (this.dfd) {
      if (closeAll === true) {
        this.dfd.reject({ closeAll: true }); // The closeAll property here alerts the cleanUpPriorityView to close all priority views
      } else {
        this.dfd.reject();
      }
    }
  }

  refreshCustomer(callback?: () => void): void {
    forkJoin([
      this.sessionStorageService.getPartner(),
      this.sessionStorageService.getPartnerUser(),
      this.customerService.get(this.customerId),
      this.websiteService.getManyByCustomerId(this.customerId)
    ])
      .subscribe(([partner, partnerUser, customer, websites]) => {
        this.customer = customer;
        this.customerObservable = ko.mapping.fromJS(customer, CustomerMapping);
        this.partner = customer.Partner;
        this.partnerName = customer.Partner.Name;
        this.partnerUser = partnerUser;
        this.setChildPageVisibility(partnerUser, partner, websites);
        this.customerInformationComponent.setContactEmail();
        if (callback) {
          callback();
        }
      });
  }

  getRefreshedCustomer(): CustomerObservable {
    return this.customerObservable;
  }

  reloadNotes() {
    if (this.notesAndTickets) {
      this.notesAndTickets.reloadNotes();
    }
  }

  reloadTickets() {
    if (this.notesAndTickets) {
      this.notesAndTickets.reloadTickets();;
    }
  }

  redirectToConfigureProducts() {
    Utils.wrapDfd(this.storeService.sso(this.customerObservable.CustomerId(), '/place-order'))
      .then(url => window.location.href = url)
      .fail(err => toastr.error(err));
  }

  private openTicket(ticketId: number): void {
    // wait a maximum of 5 seconds to load the edit ticket priority view
    let runTimes = 0;
    const ticketTimer = setInterval(() => {
      runTimes++;
      if (!PriorityViewModel.isLoadingPriorityView) {
        clearInterval(ticketTimer);
        const ticketOptions = {
          ticketId: ticketId,
          customer: this.customerObservable
        };
        setTimeout(() => {
          PriorityViewModel.show('app-components-ticket', ticketOptions).always((result: app.ticket.interfaces.IPriorityViewResult) => {
            if (result.modifiedDataFlags && result.modifiedDataFlags.customer) {
              this.refreshCustomer();
            }

            this.reloadNotes();
            this.reloadTickets();
          });
        },
          500);
      }
      if (runTimes >= 10) {
        clearInterval(ticketTimer);
      }
    },
      500);
  }

  private setChildPageVisibility(partnerUser: Boo.Objects.PartnerUser, partner: Boo.Objects.Partner, websites: Boo.Objects.Websites.Website[]) {
    if (!partnerUser) {
      toastr.error('There was an error while opening the customer.', 'Error');
      if (this.dfd) {
        this.dfd.reject();
      }

      return;
    }

    this.hasWebsite = websites.length > 0;
    this.hasOnlyEverHadBusinessProfileManagement = this.customerService.hasOnlyEverHadProduct(this.customerObservable, ProductEnum.BusinessProfileManagement);
    this.partnerSupportsReceipts = supportsReceipts.includes(this.customer.PartnerId);
    
    if (partnerUser.IsReadonly) {
      this.showStoreLink = false;
    }
  }

  private logCustomerTouch(scope: string) {
    if (this.shouldLogCustomerTouch === false) {
      return;
    }
    if (timer.getElapsedSecondsSinceLastCustomerTouch() <= 5) {
      return;
    }
    if (_.isString(scope) === false) {
      return;
    }
    if (!this.customerObservable) {
      return;
    }

    this.customerTouchService.insert(this.customerObservable.CustomerId(), timer.getElapsedSecondsSinceLastCustomerTouch(), scope)
      .subscribe({
        next: () => timer.resetCustomerTouchTime(),
        error: err => toastr.error(err)
      });
  }

  private startTimer() {
    // Start the timer utility and start the interval to keep track of elapsed time
    timer.start();
    this.customerTimoutInterval = setInterval(() => this.checkUIActivity(), 1000);
  }

  private checkUIActivity() {
    const elapsedMinutes = timer.getElapsedMinutesSinceLastUIActivity();
    if (elapsedMinutes === 60) {
      if (this.isTimeoutConfirmVisible) {
        return;
      }
      this.isTimeoutConfirmVisible = true;
      // This bandaids an issue where over time the customer has become undefined. I could not reproduce the situation to make the customer undefined.
      if (!this.customerObservable) {
        toastr.error('An error occurred and the customer will now be closed. Please reopen the customer to continue work.');
        this.shouldLogCustomerTouch = false;
        this.close(true);
        return;
      }
      const confirmMessage = `You have been inactive for over 60 minutes on Customer (${this.customerObservable.CustomerId()})</br> Name: ${this.customerObservable.Name()}</br> URL: ${this.customerObservable.Url()}</br></br>Click 'OK' to continue working on this customer.</br> Click 'Cancel' to close this customer.`;
      const inactiveTimeStamp = moment();
      bootbox.confirm(confirmMessage,
        (result: boolean) => {
          this.isTimeoutConfirmVisible = false;
          if (result === true) {
            const inactiveMilliseconds = moment().diff(inactiveTimeStamp);
            timer.addInactiveMilliseconds(inactiveMilliseconds);
            timer.resetUIActivityTime();
          } else {
            this.shouldLogCustomerTouch = false;
            this.close(true);
          }
        });
    }
  }
}
